/**
 * TeacherSupplies — bucket 3 of 4 ("My prep & supplies"). Renders the
 * design's four-module layout: Prep Checklist, My Stock, Prep List ·
 * Next 5 workshops, Incoming Resupply.
 *
 * Every module depends on the inventory schema, which doesn't exist yet
 * (no op-DB or warehouse source). The whole surface therefore renders the
 * design layout as an em-dash skeleton behind a single <MockedOverlay>,
 * mirroring RD Supplies (BON-84). It lights up when BON-151 lands.
 */
import { MOBILE_MAX, useMediaQuery } from '../../../lib/useMediaQuery';
import { adaptKpis } from '../lib/kpiAdapter';
import { KpiStrip, MockedOverlay } from '../primitives';
import { BucketError, BucketLoading } from '../regional/BucketStates';
import MobileTeacherSupplies from '../responsive/MobileTeacherSupplies';

import { useTeacherSupplies } from './teacherApi';

const EM_DASH = '—';

const SKELETON_KEYS = ['a', 'b', 'c', 'd', 'e'] as const;

const cardHeading: React.CSSProperties = {
  fontSize: 13,
  letterSpacing: '0.04em',
  margin: 0,
};

function PrepChecklistCard() {
  return (
    <section
      className="nb-card"
      style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      <div className="nb-card-hd">
        <h3 className="nb-display" style={cardHeading}>
          Prep Checklist
        </h3>
        <span style={{ fontSize: 11, color: 'var(--nb-subtle)' }}>{EM_DASH} workshops on deck</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {SKELETON_KEYS.map((k) => (
          <div
            key={k}
            style={{
              padding: '10px 12px',
              background: 'var(--nb-surface)',
              borderRadius: 8,
              borderLeft: '3px solid var(--nb-border)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                marginBottom: 6,
                color: 'var(--nb-subtle)',
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              <span>{EM_DASH}</span>
              <span>{EM_DASH}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {['Trees', '5" pots', 'Akadama'].map((col) => (
                <div key={col}>
                  <div className="nb-eyebrow" style={{ fontSize: 9, marginBottom: 2 }}>
                    {col}
                  </div>
                  <div
                    className="nb-tabular nb-mono"
                    style={{ fontSize: 13, color: 'var(--nb-subtle)' }}
                  >
                    {EM_DASH}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function MyStockCard() {
  return (
    <section
      className="nb-card"
      style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      <div className="nb-card-hd">
        <h3 className="nb-display" style={cardHeading}>
          My Stock
        </h3>
        <span style={{ fontSize: 11, color: 'var(--nb-subtle)' }}>at home base</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {SKELETON_KEYS.map((k) => (
          <div key={k}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 3,
                color: 'var(--nb-subtle)',
                fontSize: 11.5,
                fontWeight: 600,
              }}
            >
              <span>{EM_DASH}</span>
              <span className="nb-tabular nb-mono" style={{ fontSize: 10.5 }}>
                {EM_DASH}
              </span>
            </div>
            <div className="nb-bar" style={{ width: '100%' }}>
              <i style={{ width: '0%' }} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function PrepListCard() {
  return (
    <section className="nb-card" style={{ flex: '0 0 auto' }}>
      <div className="nb-card-hd">
        <h3 className="nb-display" style={cardHeading}>
          Prep List · Next 5 workshops
        </h3>
        <span className="nb-eyebrow">{EM_DASH}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {SKELETON_KEYS.map((k) => (
          <div
            key={k}
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              color: 'var(--nb-subtle)',
              fontSize: 12,
            }}
          >
            <span
              className="nb-tabular nb-mono"
              style={{ width: 36, textAlign: 'right', fontWeight: 700 }}
            >
              {EM_DASH}
            </span>
            <span style={{ flex: 1, fontWeight: 500 }}>{EM_DASH}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function IncomingResupplyCard() {
  return (
    <section className="nb-card" style={{ flex: '0 0 auto' }}>
      <div className="nb-card-hd">
        <h3 className="nb-display" style={cardHeading}>
          Incoming Resupply
        </h3>
        <span
          className="nb-pill nb-status-soft"
          style={{ height: 18, fontSize: 9.5, padding: '0 6px' }}
        >
          Scheduled
        </span>
      </div>
      <div
        style={{
          padding: 12,
          background: 'var(--nb-cream)',
          borderRadius: 8,
          border: '1px solid var(--nb-border)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <span className="nb-display" style={{ fontSize: 18, color: 'var(--nb-forest)' }}>
            {EM_DASH}
          </span>
          <span className="nb-mono" style={{ fontSize: 10, color: 'var(--nb-subtle)' }}>
            {EM_DASH}
          </span>
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--nb-muted)', marginTop: 4 }}>{EM_DASH}</div>
      </div>
    </section>
  );
}

export default function TeacherSupplies() {
  // Mobile viewport (≤480px) renders the iPhone-sized variant, same as the
  // other teacher buckets. Hook order is preserved before any early return.
  const isMobile = useMediaQuery(MOBILE_MAX);
  const q = useTeacherSupplies();

  if (isMobile) return <MobileTeacherSupplies />;
  if (q.isLoading) return <BucketLoading />;
  if (q.isError) return <BucketError error={q.error} />;
  if (!q.data) return null;
  const { data } = q.data;

  return (
    <MockedOverlay reason="Prep & inventory data isn’t wired up yet. Goes live once the inventory module ships — tracked in BON-151.">
      <KpiStrip items={adaptKpis(data.kpis)} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.55fr) minmax(0, 1fr)',
          gap: 14,
          marginTop: 16,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <PrepChecklistCard />
          <PrepListCard />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <MyStockCard />
          <IncomingResupplyCard />
        </div>
      </div>
    </MockedOverlay>
  );
}
